import { isDiscordFrame as inDiscordFrame } from "../utils/frame";
import { isLocalHost } from "../utils/frame";
import { API_FETCH_TIMEOUT_MS } from "@cookies/shared/config";

function isLocalOverride(v: string): boolean {
  const low = v.toLowerCase();
  return low.includes("localhost") || low.includes("127.0.0.1");
}

export function apiBase(): string {
  try {
    if (inDiscordFrame()) return "/api";
    const host = window.location.hostname;
    if (isLocalHost(host)) return "http://localhost:3001";
    const override = import.meta.env.VITE_API_URL as string | undefined;
    if (override && override.length > 0) {
      const trimmed = override.replace(/\/+$/, "");
      if (trimmed === "" || trimmed === "/") return "";
      if (isLocalOverride(trimmed)) return "";
      return trimmed;
    }
  } catch {
    return "";
  }
  return "";
}

export function apiUrl(path: string): string {
  const base = apiBase();
  if (base === "" || base === "/") return path;
  return base + path;
}

export function wsBase(): string {
  try {
    if (inDiscordFrame()) return "/api";
    const host = window.location.hostname;
    if (isLocalHost(host)) return "ws://localhost:3001";
    const override = import.meta.env.VITE_API_URL as string | undefined;
    if (override && override.length > 0) {
      const trimmed = override.replace(/\/+$/, "");
      if (trimmed === "" || trimmed === "/" || trimmed === "/api") return "";
      if (isLocalOverride(trimmed)) return "";
      if (trimmed.endsWith("/api")) {
        const stripped = trimmed.slice(0, -4).replace(/\/+$/, "");
        if (stripped === "" || stripped === "/") return "";
        return stripped.replace(/^http/i, "ws");
      }
      return trimmed.replace(/^http/i, "ws");
    }
  } catch {
    return "";
  }
  return "";
}

export function loginUrl(): string {
  return apiUrl("/api/auth/login");
}

export function apiError(message: string, status: number): Error & { status: number } {
  const e = new Error(message) as Error & { status: number };
  e.status = status;
  return e;
}
export function isApiError(e: unknown): e is Error & { status: number } {
  return typeof e === "object" && e !== null && "status" in e && typeof (e as { status: unknown }).status === "number" && e instanceof Error;
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
  timeoutMs?: number;
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem("cb_token");
  } catch {
    return null;
  }
}

function handleUnauthorized(path: string): void {
  if (path === "/api/auth/callback" || path === "/api/auth/discord-token") return;
  try {
    localStorage.removeItem("cb_token");
    localStorage.removeItem("cb_user");
  } catch {
    return;
  }
}

function unwrap<T>(json: unknown): T {
  if (json && typeof json === "object") {
    const r = json as Record<string, unknown>;
    if ("data" in r) return r["data"] as T;
  }
  return json as T;
}

export async function apiFetch<T>(path: string, opts?: ApiOptions): Promise<T> {
  const token = opts?.token !== undefined ? opts.token : readStoredToken();
  const external = opts?.signal;
  if (external?.aborted) throw new DOMException("Aborted", "AbortError");
  const ctrl = new AbortController();
  let timedOut = false;
  const onAbort = (): void => {
    ctrl.abort(new DOMException("Aborted", "AbortError"));
  };
  if (external) external.addEventListener("abort", onAbort, { once: true });
  const timeoutMs = opts?.timeoutMs ?? API_FETCH_TIMEOUT_MS;
  const timer = window.setTimeout(() => {
    timedOut = true;
    ctrl.abort(new DOMException("Request timed out", "TimeoutError"));
  }, timeoutMs);
  try {
    const res = await fetch(apiUrl(path), {
      method: opts?.method ?? "GET",
      credentials: "include",
      signal: ctrl.signal,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: "Bearer " + token } : {}),
      },
      ...(opts?.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    });
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      json = null;
    }
    if (!res.ok) {
      let msg = "request failed: " + res.status;
      if (json && typeof json === "object") {
        const r = json as Record<string, unknown>;
        if (typeof r["error"] === "string" && r["error"]) msg = r["error"];
        else if (typeof r["message"] === "string" && r["message"]) msg = r["message"];
      }
      if (res.status === 401) handleUnauthorized(path);
      throw apiError(msg, res.status);
    }
    if (json && typeof json === "object" && "error" in json) {
      const r = json as Record<string, unknown>;
      if (typeof r["error"] === "string" && r["error"]) throw apiError(r["error"], res.status);
    }
    return unwrap<T>(json);
  } catch (e) {
    if (timedOut) throw apiError("request timed out", 408);
    if (e instanceof DOMException && e.name === "TimeoutError") throw apiError("request timed out", 408);
    throw e;
  } finally {
    window.clearTimeout(timer);
    if (external) external.removeEventListener("abort", onAbort);
  }
}
